import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { exportAllAppointments, exportWeeklyAppointments } from '../utils/export.utils'

import DailyAppointmentsDialog from './DailyAppointmentsDialog'
import { BarChartLoading } from './Loading'

import type { Appointment } from '@/features/osa-scheduling/types'

interface WeeklyDispatchedProjectsProps {
  appointmentsData?: Appointment[]
  isLoading?: boolean
  error?: Error | null
}

interface WeekData {
  week: string
  projects: number
  startDate: Date
  endDate: Date
}

interface DayData {
  day: string
  projects: number
  date: Date
}

type ViewMode = 'weekly' | 'daily'

function getWeekRange(date: Date): { start: Date; end: Date } {
  const start = new Date(date)
  const dayOfWeek = start.getDay()
  // Adjust to make Monday the start of the week (Monday = 0, Sunday = 6)
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  start.setDate(start.getDate() + daysToMonday) // Start of week (Monday)
  start.setHours(0, 0, 0, 0)

  const end = new Date(start)
  end.setDate(end.getDate() + 6) // End of week (Sunday)
  end.setHours(23, 59, 59, 999)

  return { start, end }
}

function formatDateRange(start: Date, end: Date): string {
  const formatDate = (date: Date) => {
    const month = date.toLocaleDateString('en-US', { month: 'short' })
    const day = date.getDate()
    return `${month} ${day}`
  }

  return `${formatDate(start)} - ${formatDate(end)}`
}

function formatDayDate(date: Date): string {
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' })
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  const day = date.getDate()
  return `${dayName}, ${month} ${day}`
}

export const WeeklyDispatchedProjects: React.FC<WeeklyDispatchedProjectsProps> = ({
  appointmentsData,
  isLoading = false,
  error = null,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('weekly')
  const [selectedWeek, setSelectedWeek] = useState<WeekData | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const weeklyData = useMemo(() => {
    if (!appointmentsData || appointmentsData.length === 0) {
      return []
    }

    const appointments = appointmentsData
    const weekMap = new Map<string, WeekData>()

    // Find the earliest appointment date from those with scheduleStartTime
    const appointmentsWithTime = appointments.filter(apt => apt.scheduleStartTime)
    if (appointmentsWithTime.length === 0) {
      return []
    }

    const earliestDate = new Date(
      Math.min(...appointmentsWithTime.map(apt => new Date(apt.scheduleStartTime!).getTime()))
    )

    // Find the latest appointment date
    const latestDate = new Date(
      Math.max(...appointmentsWithTime.map(apt => new Date(apt.scheduleStartTime!).getTime()))
    )

    // Calculate number of weeks between earliest and latest dates
    const firstWeekRange = getWeekRange(earliestDate)
    const lastWeekRange = getWeekRange(latestDate)
    const weeksDiff = Math.round(
      (lastWeekRange.start.getTime() - firstWeekRange.start.getTime()) / (7 * 24 * 60 * 60 * 1000)
    )

    // Generate weeks from first to last appointment week
    const weeks: WeekData[] = []
    const numWeeks = Math.max(weeksDiff, 0) // Start from 0

    for (let i = 0; i <= numWeeks; i++) {
      const weekStart = new Date(firstWeekRange.start)
      weekStart.setDate(weekStart.getDate() + i * 7)

      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6)
      weekEnd.setHours(23, 59, 59, 999)

      const weekKey = formatDateRange(weekStart, weekEnd)

      weeks.push({
        week: weekKey,
        projects: 0,
        startDate: weekStart,
        endDate: weekEnd,
      })

      weekMap.set(weekKey, weeks[weeks.length - 1])
    }

    // Count appointments in each week
    appointments.forEach(appointment => {
      if (!appointment.scheduleStartTime) return

      const appointmentDate = new Date(appointment.scheduleStartTime)
      const weekRange = getWeekRange(appointmentDate)
      const weekKey = formatDateRange(weekRange.start, weekRange.end)

      const weekData = weekMap.get(weekKey)
      if (weekData) {
        weekData.projects++
      }
    })

    return weeks
  }, [appointmentsData])

  const dailyData = useMemo(() => {
    if (!selectedWeek || !appointmentsData) {
      return []
    }

    const days: DayData[] = []
    const dayMap = new Map<string, DayData>()

    // Generate all 7 days of the week
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(selectedWeek.startDate)
      dayDate.setDate(dayDate.getDate() + i)
      dayDate.setHours(0, 0, 0, 0)

      const dayKey = formatDayDate(dayDate)
      const dayData = {
        day: dayKey,
        projects: 0,
        date: dayDate,
      }

      days.push(dayData)
      dayMap.set(dayDate.toDateString(), dayData)
    }

    // Count appointments for each day
    appointmentsData.forEach(appointment => {
      if (!appointment.scheduleStartTime) return

      const appointmentDate = new Date(appointment.scheduleStartTime)

      // Check if appointment is within the selected week
      if (appointmentDate >= selectedWeek.startDate && appointmentDate <= selectedWeek.endDate) {
        const dayKey = appointmentDate.toDateString()
        const dayData = dayMap.get(dayKey)
        if (dayData) {
          dayData.projects++
        }
      }
    })

    return days
  }, [selectedWeek, appointmentsData])

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode)
      if (newMode === 'weekly') {
        setSelectedWeek(null)
      } else if (newMode === 'daily' && !selectedWeek && weeklyData.length > 0) {
        // When switching to daily view, select the first week if no week is selected
        setSelectedWeek(weeklyData[0])
      }
    }
  }

  const handleBarClick = (_data: unknown, index: number) => {
    if (viewMode === 'weekly' && weeklyData[index]) {
      setSelectedWeek(weeklyData[index])
      setViewMode('daily')
    } else if (viewMode === 'daily' && dailyData[index]) {
      setSelectedDate(dailyData[index].date)
      setDialogOpen(true)
    }
  }

  const handlePreviousWeek = () => {
    if (!selectedWeek || weeklyData.length === 0) return

    const currentIndex = weeklyData.findIndex(w => w.week === selectedWeek.week)
    if (currentIndex > 0) {
      setSelectedWeek(weeklyData[currentIndex - 1])
    }
  }

  const handleNextWeek = () => {
    if (!selectedWeek || weeklyData.length === 0) return

    const currentIndex = weeklyData.findIndex(w => w.week === selectedWeek.week)
    if (currentIndex < weeklyData.length - 1) {
      setSelectedWeek(weeklyData[currentIndex + 1])
    }
  }

  const canGoPrevious = useMemo(() => {
    if (!selectedWeek || weeklyData.length === 0) return false
    const currentIndex = weeklyData.findIndex(w => w.week === selectedWeek.week)
    return currentIndex > 0
  }, [selectedWeek, weeklyData])

  const canGoNext = useMemo(() => {
    if (!selectedWeek || weeklyData.length === 0) return false
    const currentIndex = weeklyData.findIndex(w => w.week === selectedWeek.week)
    return currentIndex < weeklyData.length - 1
  }, [selectedWeek, weeklyData])

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (format: 'csv' | 'xlsx') => {
    if (!appointmentsData) return

    if (viewMode === 'weekly') {
      // Export all appointments
      exportAllAppointments(appointmentsData, format)
    } else {
      // Export appointments for the selected week only
      if (!selectedWeek) return

      const weekAppointments = appointmentsData.filter(appointment => {
        if (!appointment.scheduleStartTime) return false
        const appointmentDate = new Date(appointment.scheduleStartTime)
        return appointmentDate >= selectedWeek.startDate && appointmentDate <= selectedWeek.endDate
      })

      exportWeeklyAppointments(
        weekAppointments,
        { start: selectedWeek.startDate, end: selectedWeek.endDate },
        format
      )
    }

    handleExportClose()
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography color="error">Error loading weekly dispatched projects data</Typography>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return <BarChartLoading title="Weekly Dispatched Projects Overview" />
  }

  const chartData = viewMode === 'weekly' ? weeklyData : dailyData
  const maxValue = Math.max(...chartData.map(d => d.projects), 10)
  const yAxisMax = Math.ceil(maxValue / 5) * 5 // Round up to nearest 5

  return (
    <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#2D3748',
            }}
          >
            {viewMode === 'weekly'
              ? 'Weekly Dispatched Projects Overview'
              : `Daily View - Week of ${selectedWeek?.week || ''}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {chartData.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 2,
                },
              }}
            >
              <ToggleButton value="weekly">Weekly</ToggleButton>
              <ToggleButton value="daily">Daily</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {((viewMode === 'weekly' && weeklyData.length > 0) ||
          (viewMode === 'daily' && selectedWeek)) && (
          <Box sx={{ width: '100%', height: 320, position: 'relative' }}>
            {viewMode === 'daily' && (
              <>
                {canGoPrevious && (
                  <IconButton
                    onClick={handlePreviousWeek}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '35%',
                      transform: 'translateY(-50%)',
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                )}
                {canGoNext && (
                  <IconButton
                    onClick={handleNextWeek}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '35%',
                      transform: 'translateY(-50%)',
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                )}
              </>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 20,
                }}
              >
                <XAxis
                  dataKey={viewMode === 'weekly' ? 'week' : 'day'}
                  tick={{ fill: '#718096', fontSize: 11 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fill: '#718096' }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  domain={[0, yAxisMax]}
                  ticks={Array.from({ length: 5 }, (_, i) => Math.round((yAxisMax / 4) * i))}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#2D3748', fontWeight: 600 }}
                  itemStyle={{ color: '#4A90E2' }}
                  formatter={(value: number) => [`${value} projects`, 'Dispatched']}
                />
                <Bar
                  dataKey="projects"
                  fill="#4A90E2"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                  style={{ cursor: 'pointer' }}
                  onClick={handleBarClick}
                >
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>

      <DailyAppointmentsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        selectedDate={selectedDate}
        appointments={appointmentsData || []}
      />
    </Card>
  )
}

export default WeeklyDispatchedProjects
